import type { TFunction } from 'i18next';
import type { LucideIcon } from 'lucide-react';
import { User, CreditCard, Bell, Award, LifeBuoy } from 'lucide-react';

export const SETTINGS_NAV_SEGMENT_ORDER = [
  'profile',
  'plan',
  'notifications',
  'progress',
  'help',
] as const;

export type SettingsNavSegment = (typeof SETTINGS_NAV_SEGMENT_ORDER)[number];

/** Section titles for nav + settings shell; uses explicit ns so i18n validation picks up keys. */
export function settingsNavSectionLabel(t: TFunction, segment: SettingsNavSegment): string {
  switch (segment) {
    case 'profile':
      return t('sections.profile', { ns: 'settings' });
    case 'plan':
      return t('sections.plan', { ns: 'settings' });
    case 'notifications':
      return t('sections.notifications', { ns: 'settings' });
    case 'progress':
      return t('sections.progress', { ns: 'settings' });
    case 'help':
      return t('sections.help', { ns: 'settings' });
  }
}

export const SETTINGS_SECTIONS = new Set<string>(SETTINGS_NAV_SEGMENT_ORDER);

export function settingsPath(segment: SettingsNavSegment): `/settings/${SettingsNavSegment}` {
  return `/settings/${segment}`;
}

export type SettingsNavGroup = {
  groupLabelKey: string;
  items: ReadonlyArray<{ segment: SettingsNavSegment; icon: LucideIcon }>;
};

/** Ordered groups for account menu and settings shell (IA: account → billing → prefs → progress/help). */
export const SETTINGS_NAV_GROUPS: ReadonlyArray<SettingsNavGroup> = [
  {
    groupLabelKey: 'nav.menuGroups.account',
    items: [{ segment: 'profile', icon: User }],
  },
  {
    groupLabelKey: 'nav.menuGroups.planBilling',
    items: [{ segment: 'plan', icon: CreditCard }],
  },
  {
    groupLabelKey: 'nav.menuGroups.preferences',
    items: [{ segment: 'notifications', icon: Bell }],
  },
  {
    groupLabelKey: 'nav.menuGroups.progressHelp',
    items: [
      { segment: 'progress', icon: Award },
      { segment: 'help', icon: LifeBuoy },
    ],
  },
];

export function flattenSettingsNavItems(): Array<{ segment: SettingsNavSegment; icon: LucideIcon }> {
  return SETTINGS_NAV_GROUPS.flatMap((g) => [...g.items]);
}
